import { useEffect } from 'react';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Building2, CheckCircle2, Clock, Sparkles, Briefcase, ShieldCheck, ClipboardCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCmsContent, parsePoints } from '../context/CmsImagesContext';

export default function BusinessCleaning() {
  const { t, language } = useLanguage();
  const { getImage, getText, getHtml } = useCmsContent();

  useEffect(() => {
    document.title = t({ da: "FTR Service ApS - Erhvervsrengøring", en: "FTR Service ApS - Business Cleaning" });
  }, [t]);

  const benefitIcons = [
    <Building2 className="w-6 h-6" />,
    <Briefcase className="w-6 h-6" />,
    <ShieldCheck className="w-6 h-6" />,
    <Sparkles className="w-6 h-6" />
  ];

  // 1. Tjek individuelle loesninger_punkt_1..4 felter fra CMS (mest robuste løsning for kunden)
  const defaultBenefitList = [
    { 
      title: t({ da: 'Kontorrengøring', en: 'Office cleaning' }), 
      text: t({ da: 'Alt fra mindre kontorer til større erhvervsejendomme.', en: 'Everything from small offices to larger commercial properties.' }), 
      icon: <Building2 className="w-6 h-6" /> 
    },
    { 
      title: t({ da: 'Fast Kontaktperson', en: 'Fixed Contact Person' }), 
      text: t({ da: 'I får tilknyttet en fast person, der kender jeres lokaler ud og ind.', en: 'You will be assigned a fixed person who knows your premises inside out.' }), 
      icon: <Briefcase className="w-6 h-6" /> 
    },
    { 
      title: t({ da: 'Kvalitetsrengøring', en: 'Quality Cleaning' }), 
      text: t({ da: 'Vi leverer altid rengøring af højeste kvalitet, der lever op til jeres forventninger.', en: 'We always deliver cleaning of the highest quality that meets your expectations.' }), 
      icon: <ShieldCheck className="w-6 h-6" /> 
    },
    { 
      title: t({ da: 'Miljøvenlige Midler', en: 'Eco-friendly Products' }), 
      text: t({ da: 'Vi bruger skånsomme produkter, der passer på miljøet og jeres inventar.', en: 'We use gentle products that take care of the environment and your inventory.' }), 
      icon: <Sparkles className="w-6 h-6" /> 
    }
  ];

  const individualBenefits = [];
  for (let i = 1; i <= 4; i++) {
    const def = defaultBenefitList[i - 1];
    const title = getText('erhvervsrengoring', `loesninger_punkt_${i}_titel`, getText('erhverv_loesninger', `punkt_${i}_titel`, def ? def.title : ''));
    const text = getText('erhvervsrengoring', `loesninger_punkt_${i}_tekst`, getText('erhverv_loesninger', `punkt_${i}_tekst`, def ? def.text : ''));
    if (title || text) {
      individualBenefits.push({
        title,
        text,
        icon: benefitIcons[(i - 1) % benefitIcons.length],
      });
    }
  }

  let benefits = individualBenefits;

  // 2. Hvis ikke sat via individuelle felter, parse loesninger_punkter
  if (benefits.length === 0) {
    const punkterRaw = getText('erhvervsrengoring', 'loesninger_punkter', getText('erhverv_loesninger', 'punkter', ''));
    const parsedBenefits = parsePoints(punkterRaw, language);
    if (parsedBenefits.length > 0) {
      benefits = parsedBenefits.map((pt, idx) => ({
        title: pt.title,
        text: pt.text || pt.full,
        icon: benefitIcons[idx % benefitIcons.length],
      }));
    }
  }

  // 3. Fallback til standard punkter
  if (benefits.length === 0) {
    benefits = defaultBenefitList;
  }

  // Forbered tjeklistepunkter (både fra richtext og multiline tekst)
  const defaultChecklistItems = [
    t({ da: 'Grundig støvsugning og gulvvask', en: 'Thorough vacuuming and floor washing' }),
    t({ da: 'Aftørring af alle overflader og døre', en: 'Wiping of all surfaces and doors' }),
    t({ da: 'Rengøring af køkken og kantine', en: 'Cleaning of kitchen and canteen' }),
    t({ da: 'Sanitet og badeværelsesrengøring', en: 'Sanitation and bathroom cleaning' }),
    t({ da: 'Tømning af skraldespande', en: 'Emptying of waste bins' }),
    t({ da: 'Afstøvning af IT-udstyr og skærme', en: 'Dusting of IT equipment and monitors' }),
    t({ da: 'Vandret fladeaftørring (borde/reoler)', en: 'Horizontal surface wiping (tables/shelves)' }),
    t({ da: 'Opfyldning af forbrugsvarer', en: 'Refilling of consumables' })
  ];

  const tjeklisteTekst = getHtml('erhvervsrengoring', 'pakke_tjekliste', getHtml('erhverv_pakke', 'tekst', ''));
  const tjeklistePunkterRaw = getText('erhvervsrengoring', 'pakke_punkter', getText('erhverv_pakke', 'punkter', ''));
  const parsedChecklist = parsePoints(tjeklisteTekst || tjeklistePunkterRaw, language);
  let checklistItems = [];
  if (parsedChecklist.length > 0) {
    checklistItems = parsedChecklist.map((pt) => pt.full || pt.text);
  }

  if (checklistItems.length === 0 || (language === 'en' && parsedChecklist.length > 0 && parsedChecklist.every(p => !p.text || /[\u00C0-\u017F]/.test(p.text) || /og|af|alle/.test(p.text)))) {
    checklistItems = defaultChecklistItems;
  }

  // Forbered fordele (Hvorfor vælge os? - 01, 02, 03)
  const defaultFordeleItems = [
    { num: '01', title: t({ da: "Pålidelighed", en: "Reliability" }), text: t({ da: "Vi overholder altid vores aftaler og tidsplaner, så jeres hverdag flyder uforstyrret.", en: "We always adhere to our agreements and schedules, so your everyday life flows undisturbed." }) },
    { num: '02', title: t({ da: "Høj Standard", en: "High Standard" }), text: t({ da: "Vores team er trænet i de nyeste rengøringsmetoder og bruger kun de bedste produkter.", en: "Our team is trained in the latest cleaning methods and uses only the best products." }) },
    { num: '03', title: t({ da: "Fleksibilitet", en: "Flexibility" }), text: t({ da: "Vi gør rent, når det passer jer – enten før, i eller efter arbejdstid.", en: "We clean when it suits you – either before, during or after working hours." }) }
  ];

  const individualFordele = [];
  for (let i = 1; i <= 3; i++) {
    const def = defaultFordeleItems[i - 1];
    const title = getText('erhvervsrengoring', `fordele_punkt_${i}_titel`, getText('erhverv_hvorfor_os', `punkt_${i}_titel`, def ? def.title : ''));
    const text = getText('erhvervsrengoring', `fordele_punkt_${i}_tekst`, getText('erhverv_hvorfor_os', `punkt_${i}_tekst`, def ? def.text : ''));
    if (title || text) {
      individualFordele.push({
        num: String(i).padStart(2, '0'),
        title,
        text
      });
    }
  }

  let fordeleItems = individualFordele;
  if (fordeleItems.length === 0) {
    const fordeleRaw = getText('erhvervsrengoring', 'fordele_punkter', getText('erhverv_hvorfor_os', 'punkter', ''));
    const parsedFordele = parsePoints(fordeleRaw, language);
    if (parsedFordele.length > 0) {
      fordeleItems = parsedFordele.map((pt, idx) => ({
        num: String(idx + 1).padStart(2, '0'),
        title: pt.title,
        text: pt.text || pt.full
      }));
    }
  }

  if (fordeleItems.length === 0 || (language === 'en' && individualFordele.length > 0 && individualFordele.every(f => /[\u00C0-\u017F]/.test(f.title) || /aftaler|metoder/.test(f.text)))) {
    fordeleItems = defaultFordeleItems;
  }

  // CTA-citat
  const rawCitat = getText('erhvervsrengoring', 'cta_citat', getText('erhverv_cta', 'citat', t({ da: "Vi skaber de bedste rammer for jeres medarbejdere.", en: "We create the best framework for your employees." })));
  const cleanCitat = typeof rawCitat === 'string' ? rawCitat.replace(/^["'“”«»]+|["'“”«»]+$/g, '').trim() : rawCitat;

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={getImage('erhverv_hero_billede', getImage('business_hero', '/images/business-cleaning.png', { width: 1400 }))} 
            alt="" 
            className="w-full h-full object-cover opacity-30" 
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide uppercase bg-brand-primary/10 text-brand-primary mb-6">
              {getText('erhvervsrengoring', 'hero_underoverskrift', getText('erhverv_hero', 'underoverskrift', t({ da: "Erhvervsrengøring", en: "Business Cleaning" })))}
            </span>
            <h1 className="text-4xl sm:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              {getText('erhvervsrengoring', 'hero_overskrift', getText('erhverv_hero', 'overskrift', t({ da: "Erhvervsrengøring med fokus på kvalitet", en: "Business cleaning with focus on quality" })))}
            </h1>
            {getText('erhvervsrengoring', 'hero_tekst', getText('erhverv_hero', 'tekst', null)) ? (
              <div 
                className="text-xl text-gray-600 mb-10 leading-relaxed [&>p]:mb-4"
                dangerouslySetInnerHTML={{ __html: getHtml('erhvervsrengoring', 'hero_tekst', getHtml('erhverv_hero', 'tekst', t({
                  da: '<p>Professionel rengøring skaber rene, sunde og indbydende omgivelser, hvor både medarbejdere og gæster trives. Vi sørger for, at din virksomhed altid fremstår præsentabel.</p>',
                  en: '<p>Professional cleaning creates clean, healthy and inviting surroundings where both employees and guests thrive. We ensure that your business always appears presentable.</p>'
                }))) }}
              />
            ) : (
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                {t({ 
                  da: "Professionel rengøring skaber rene, sunde og indbydende omgivelser, hvor både medarbejdere og gæster trives. Vi sørger for, at din virksomhed altid fremstår præsentabel.",
                  en: "Professional cleaning creates clean, healthy and inviting surroundings where both employees and guests thrive. We ensure that your business always appears presentable."
                })}
              </p>
            )}
            <div className="flex gap-4">
              <Link 
                to={getText('erhvervsrengoring', 'hero_knap_link', getText('erhverv_hero', 'knap_link', '/kontakt'))} 
                className="bg-[#85d7f9] text-white px-10 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all shadow-xl shadow-[#85d7f9]/20"
              >
                {getText('erhvervsrengoring', 'hero_knap_tekst', getText('erhverv_hero', 'knap_tekst', t({ da: "Få et erhvervstilbud", en: "Get a business quote" })))}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-[#85d7f9]/20 rounded-3xl -z-0"></div>
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#85d7f9]/10 rounded-full blur-2xl -z-0"></div>
                
                <img 
                  src={getImage('erhverv_loesninger_billede', getImage('business_office', '/images/office-cleaning.png', { width: 900 }))} 
                  alt={t({ da: "Professionel kontorrengøring", en: "Professional office cleaning" })} 
                  className="rounded-3xl shadow-2xl relative z-10" 
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                {getText('erhvervsrengoring', 'loesninger_overskrift', getText('erhverv_loesninger', 'overskrift', t({ da: "Skræddersyede løsninger til jeres virksomhed", en: "Tailored solutions for your business" })))}
              </h2>
              <ul className="space-y-6">
                {benefits.map((benefit, idx) => (
                  <li key={idx} className="flex gap-4">
                    <div className="bg-white text-brand-primary p-3 rounded-xl shadow-sm border border-gray-100 shrink-0 h-fit">
                      {benefit.icon}
                    </div>
                    <div>
                      {benefit.title && <h4 className="font-bold text-gray-900 mb-1">{benefit.title}</h4>}
                      <p className="text-gray-600">{benefit.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Checklist Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 bg-white border border-gray-100 shadow-xl rounded-[3rem] p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-2 -mr-2 bg-[#85d7f9] text-white px-8 py-2 rounded-full font-bold shadow-lg shadow-[#85d7f9]/20 transform rotate-3">
            {getText('erhvervsrengoring', 'pakke_badge', getText('erhverv_pakke', 'underoverskrift', t({ da: "Erhvervspakke", en: "Business Package" })))}
          </div>
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900">
              {getText('erhvervsrengoring', 'pakke_overskrift', getText('erhverv_pakke', 'overskrift', t({ da: "Hvad er inkluderet?", en: "What is included?" })))}
            </h3>
            <p className="text-gray-500 mt-2">
              {getText('erhvervsrengoring', 'pakke_underoverskrift', t({ da: "Dette er standard i vores erhvervsaftaler", en: "This is standard in our business agreements" }))}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6">
            {checklistItems.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-brand-primary shrink-0" />
                <span className="text-gray-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-8 border-t border-gray-100 text-center">
            {cleanCitat && (
              <p className="text-gray-600 mb-8 italic">"{cleanCitat}"</p>
            )}
            <Link 
              to={getText('erhvervsrengoring', 'cta_knap_link', getText('erhverv_cta', 'knap_link', '/kontakt'))} 
              className="bg-[#85d7f9] text-white px-10 py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-all shadow-lg shadow-[#85d7f9]/20"
            >
              {getText('erhvervsrengoring', 'cta_knap_tekst', getText('erhverv_cta', 'knap_tekst', t({ da: "Få et uforpligtende tilbud", en: "Get a non-binding quote" })))}
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Blocks */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {getText('erhvervsrengoring', 'fordele_overskrift', getText('erhverv_hvorfor_os', 'overskrift', t({ da: "Hvorfor vælge os?", en: "Why choose us?" })))}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {fordeleItems.map((item, idx) => (
              <div key={idx} className="bg-white p-10 rounded-3xl text-center shadow-sm border border-gray-100">
                <div className="w-16 h-16 bg-brand-primary/10 text-brand-primary rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  {item.num}
                </div>
                <h4 className="text-xl font-bold mb-4">{item.title}</h4>
                <p className="text-gray-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
